/**
 * What the forecast run says the business is worth.
 *
 * The valuation half of a view that used to do both jobs. `accounting/ForecastViewV2`
 * fits posteriors for the accounting drivers and writes them into the inputs; this
 * reads the enterprise value the SAME run produced, and what moves it.
 *
 * ONE PIPELINE, TWO VIEWS. There is no second run and no second endpoint. The
 * distribution is persisted on the model by whichever view asked for it, and both read
 * it from the store. Running it twice would double a cost the ops notes measure in
 * dollars, and the two halves come from the same Monte Carlo samples — a second run
 * would disagree with the first for no reason a user could see. So this view can start
 * a run, and says plainly that doing so also produces the accounting forecast.
 *
 * A RANGE FIRST, a median second. It comes out of a simulation, and a single headline
 * figure would imply a precision the method does not have. That was true when this
 * markup lived in the forecast view and it is the reason it is worth keeping intact.
 */

import { WorkingIndicator } from '../../../components/WorkingIndicator';
import { CollapsibleNotes } from '../CollapsibleNotes';
import { formatMoney } from '../../../lib/currency';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { ProposedAssumptions } from './ProposedAssumptions';

function money(value: number | undefined, currency: string): string {
  if (value === undefined) return '—';
  return formatMoney(value, currency);
}

export function AiValuationViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const forecast = useFinancialModelV2Store((s) => s.forecast);
  const isForecasting = useFinancialModelV2Store((s) => s.isForecasting);
  const forecastProgress = useFinancialModelV2Store((s) => s.forecastProgress);
  const error = useFinancialModelV2Store((s) => s.forecastError);
  const runForecast = useFinancialModelV2Store((s) => s.runForecast);

  if (!model) return null;

  const currency = model.settings.baseCurrency;
  const ev = forecast?.enterpriseValue;
  const sensitivity = forecast?.sensitivity ?? {};
  const shares = model.settings.valuationConfig?.sharesOutstanding ?? 0;

  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-1 text-lg font-bold text-gray-800">AI valuation</h2>
      <p className="mb-4 max-w-3xl text-sm text-gray-500">
        A Monte Carlo discounted cash flow over the forecast this model's history
        supports. It gives a range rather than a figure, because that is what a
        simulation produces.
      </p>

      <div className="mb-5 flex gap-2">
        <button
          type="button"
          disabled={isForecasting}
          onClick={() => void runForecast(false)}
          className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {isForecasting
            ? 'Valuing…'
            : forecast
              ? 'Run again'
              : 'Run the valuation'}
        </button>
        {forecast && (
          <button
            type="button"
            disabled={isForecasting}
            onClick={() => void runForecast(true)}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Regenerate (ignore cache)
          </button>
        )}
      </div>

      {/* Said here rather than discovered afterwards. The same run produces the
          accounting forecast, and someone in the valuation discipline has no reason
          to expect that a valuation also changes what the forecast tab shows. */}
      <p className="mb-4 max-w-3xl text-xs text-gray-500">
        One run produces both this and the accounting forecast — they come from the
        same simulation, so they cannot disagree. Neither is written into the model
        until it is applied, which is done from Accounting &rsaquo; AI Forecast.
      </p>

      {isForecasting && (
        <div className="mb-4 max-w-3xl rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <WorkingIndicator
            label="Running four forecasters and a Monte Carlo simulation"
            detail={forecastProgress || undefined}
          />
          <p className="mt-1.5 text-xs text-blue-800">
            This takes a minute or two the first time; repeat runs are cached.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 max-w-3xl rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {!forecast && !isForecasting && !error && (
        <p className="max-w-3xl text-sm text-gray-500">
          Nothing has been run yet. This needs history to forecast from — an
          imported filing has it; a model built by hand may not.
        </p>
      )}

      {forecast && (
        <div className="max-w-4xl space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Enterprise value range
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {money(ev?.p25, currency)} – {money(ev?.p75, currency)}
            </div>
            <div className="mt-1 text-sm text-gray-600">
              Median {money(ev?.median, currency)} · 5th–95th percentile{' '}
              {money(ev?.p5, currency)} – {money(ev?.p95, currency)}
            </div>
            {shares > 0 && ev?.median !== undefined && (
              <div className="mt-1 text-sm text-gray-600">
                Median per share {money(ev.median / shares, currency)} on{' '}
                {shares.toLocaleString()} shares — before net debt, so this is an
                enterprise figure, not an equity one.
              </div>
            )}
            <div className="mt-2 text-xs text-gray-500">
              From {forecast.nSamples.toLocaleString()} simulations
              {forecast.cacheHit && ' · served from cache'}
            </div>
          </div>

          <CollapsibleNotes
            title="Points to check"
            notes={forecast.forecasterWarnings}
          />

          {Object.keys(sensitivity).length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700">
                What moves the value
              </div>
              <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
                How much the enterprise value moves with each driver. The largest is
                where an argument about this valuation is worth having.
              </p>
              <div className="divide-y divide-gray-100">
                {Object.entries(sensitivity)
                  .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
                  .map(([driver, value]) => (
                    <div
                      key={driver}
                      className="flex items-center justify-between px-4 py-2 text-sm"
                    >
                      <span className="text-gray-700">{driver}</span>
                      <span className="tabular-nums text-gray-900">
                        {value.toFixed(3)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Outside the block above on purpose. These come from the statements, not from
          the simulation, so they can be asked for before anything has been run - which
          is exactly when someone has no view on a discount rate. */}
      <ProposedAssumptions />
    </div>
  );
}
